import { useState } from "react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Save, X, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";


export default function TickersTab({ tickers, onRefresh }) {
  const [tickerForm, setTickerForm] = useState({
    text: "",
    link_url: "",
    order: 0,
    active: true
  });
  const [editingTicker, setEditingTicker] = useState(null);
  
  // Bulk delete state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  const handleTickerSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTicker) {
        await adminApi.put(`/tickers/${editingTicker.id}`, tickerForm);
        toast.success("Ticker actualizado");
      } else {
        await adminApi.post(`/tickers`, tickerForm);
        toast.success("Ticker creado");
      }
      resetTickerForm();
      onRefresh();
    } catch (error) {
      toast.error("Error al guardar ticker");
    }
  };

  const handleEditTicker = (ticker) => {
    setEditingTicker(ticker);
    setTickerForm({
      text: ticker.text,
      link_url: ticker.link_url || "",
      order: ticker.order,
      active: ticker.active
    });
  };

  const handleDeleteTicker = async (tickerId) => {
    if (!window.confirm("¿Eliminar este ticker?")) return;
    try {
      await adminApi.delete(`/tickers/${tickerId}`);
      toast.success("Ticker eliminado");
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(tickerId);
        return newSet;
      });
      onRefresh();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error(`Error: ${errorMsg}`);
    }
  };

  // Bulk delete functions
  const toggleSelection = (id) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedItems(new Set(tickers.map(t => t.id)));
  };

  const deselectAll = () => {
    setSelectedItems(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    
    const confirmMessage = `¿Eliminar ${selectedItems.size} ticker${selectedItems.size > 1 ? 's' : ''}? Esta acción no se puede deshacer.`;
    if (!window.confirm(confirmMessage)) return;

    setIsDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    const itemIds = Array.from(selectedItems);
    
    for (const itemId of itemIds) {
      try {
        await adminApi.delete(`/tickers/${itemId}`);
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }

    setIsDeleting(false);
    setSelectedItems(new Set());

    if (successCount > 0) {
      toast.success(`${successCount} ticker${successCount > 1 ? 's' : ''} eliminado${successCount > 1 ? 's' : ''}`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} ticker${errorCount > 1 ? 's' : ''} no se pud${errorCount > 1 ? 'ieron' : 'o'} eliminar`);
    }
    
    onRefresh();
  };

  const resetTickerForm = () => {
    setEditingTicker(null);
    setTickerForm({ text: "", link_url: "", order: 0, active: true });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      {/* Ticker Form */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm" data-testid="ticker-form-card">
        <h2 className="text-xl font-bold mb-6 flex items-center gap-2 text-black">
          {editingTicker ? <Edit2 size={20} /> : <Plus size={20} />}
          {editingTicker ? "Editar Ticker" : "Nuevo Ticker"}
        </h2>
        <form onSubmit={handleTickerSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-600 mb-2 font-medium">Texto del ticker *</label>
            <Input
              value={tickerForm.text}
              onChange={(e) => setTickerForm({...tickerForm, text: e.target.value})}
              placeholder="¡Nuevo episodio disponible!"
              required
              className="bg-gray-50 border-gray-200 text-black focus:border-[#E91E8C]"
              data-testid="ticker-text-input"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-2 font-medium">URL de enlace (opcional)</label>
            <Input
              value={tickerForm.link_url}
              onChange={(e) => setTickerForm({...tickerForm, link_url: e.target.value})}
              placeholder="https://..."
              className="bg-gray-50 border-gray-200 text-black focus:border-[#E91E8C]"
              data-testid="ticker-link-input"
            />
            <p className="text-xs text-gray-400 mt-1">Si agregas URL, el texto será clickeable.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2 font-medium">Orden</label>
              <Input
                type="number"
                value={tickerForm.order}
                onChange={(e) => setTickerForm({...tickerForm, order: parseInt(e.target.value) || 0})}
                className="bg-gray-50 border-gray-200 text-black focus:border-[#E91E8C]"
                data-testid="ticker-order-input"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2 font-medium">Estado</label>
              <Select
                value={tickerForm.active ? "active" : "inactive"}
                onValueChange={(value) => setTickerForm({...tickerForm, active: value === "active"})}
              >
                <SelectTrigger className="bg-gray-50 border-gray-200 text-black" data-testid="ticker-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="inactive">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="submit" className="bg-black hover:bg-gray-800 text-white rounded-full" data-testid="ticker-submit-btn">
              <Save size={16} className="mr-2" />
              {editingTicker ? "Actualizar" : "Crear"}
            </Button>
            {editingTicker && (
              <Button type="button" variant="outline" onClick={resetTickerForm} className="rounded-full" data-testid="ticker-cancel-btn">
                <X size={16} className="mr-2" />
                Cancelar
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Tickers List */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm" data-testid="tickers-list">
        <h2 className="text-xl font-bold mb-4 text-black">
          Ticker de Noticias
          <span className="text-gray-400 font-normal text-base ml-2">({tickers.length})</span>
        </h2>
        
        {/* Bulk Selection Controls */}
        {tickers.length > 0 && (
          <div className="flex items-center justify-between mb-4 p-3 bg-red-50 rounded-xl border border-red-100">
            <div className="flex items-center gap-3">
              <span className="text-sm text-red-700 font-medium">
                {selectedItems.size} seleccionado{selectedItems.size !== 1 ? 's' : ''}
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={selectAll}
                className="text-xs text-red-600 hover:text-red-800"
                disabled={isDeleting}
              >
                Seleccionar todos ({tickers.length})
              </Button>
              {selectedItems.size > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={deselectAll}
                  className="text-xs text-gray-500 hover:text-gray-700"
                  disabled={isDeleting}
                >
                  Deseleccionar
                </Button>
              )}
            </div>
            {selectedItems.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="bg-red-500 hover:bg-red-600"
                data-testid="bulk-delete-tickers-btn"
              >
                {isDeleting ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} className="mr-2" />
                    Eliminar {selectedItems.size}
                  </>
                )}
              </Button>
            )}
          </div>
        )}

        {tickers.length === 0 ? (
          <p className="text-gray-500">No hay tickers. Crea uno para comenzar.</p>
        ) : (
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {tickers.map((ticker) => {
              const isSelected = selectedItems.has(ticker.id);
              return (
                <div 
                  key={ticker.id}
                  className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                    isSelected 
                      ? 'bg-red-50 border-red-300' 
                      : ticker.active 
                        ? 'bg-gray-50 border-gray-100' 
                        : 'bg-gray-100 border-gray-200 opacity-60'
                  }`}
                  data-testid={`ticker-item-${ticker.id}`}
                >
                  {/* Checkbox */}
                  <button
                    type="button"
                    onClick={() => toggleSelection(ticker.id)}
                    disabled={isDeleting}
                    className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 transition-colors mr-3 ${
                      isSelected 
                        ? 'bg-red-500 border-red-500 text-white' 
                        : 'bg-white border-gray-300 hover:border-red-400'
                    }`}
                    data-testid={`select-ticker-${ticker.id}`}
                  >
                    {isSelected && <Check size={12} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-black truncate pr-4">{ticker.text}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500">Orden: {ticker.order}</span>
                      {ticker.link_url && (
                        <span className="text-xs text-blue-500 truncate max-w-[150px]">
                          {ticker.link_url}
                        </span>
                      )}
                      {!ticker.active && <span className="text-xs text-gray-400">(Inactivo)</span>}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditTicker(ticker)}
                      className="text-gray-600 hover:text-black"
                      data-testid={`edit-ticker-${ticker.id}`}
                    >
                      <Edit2 size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteTicker(ticker.id)}
                      data-testid={`delete-ticker-${ticker.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
